import React, { useState } from "react";
import { UserX, Unlock } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

// Lot 629 — sanction « compte non suivable » à durée limitée (Admin › Utilisateurs › Gérer), levée automatique à l'échéance.
const DURATIONS = [["24h", "24 heures"], ["7d", "7 jours"], ["30d", "30 jours"], ["permanent", "Illimité"], ["custom", "Jusqu'à une date…"]];
const MUTED = { color: "var(--yalola-text-muted)" };
const AMBER = "#B45309";
const fmt = (s) => new Date(s).toLocaleString("fr-FR", { dateStyle: "medium", timeStyle: "short" });
const localMin = () => { const d = new Date(Date.now() + 10 * 60000); d.setSeconds(0, 0); return new Date(d - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16); };

export const followLockActive = (u) => !!u?.follow_locked && (!u.follow_locked_until || new Date(u.follow_locked_until) > new Date());

export const FollowLockCard = ({ user: u, onChanged }) => {
  const [dur, setDur] = useState("7d");
  const [until, setUntil] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const active = followLockActive(u);

  const call = async (payload, okMsg) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/users/${u.id}/follow-lock`, payload);
      toast.success(okMsg(data));
      setReason(""); setUntil("");
      onChanged?.();
    } catch (e) { toast.error(e?.friendlyMessage || "Erreur"); }
    finally { setBusy(false); }
  };
  const lock = () => {
    if (dur === "custom" && !until) { toast.error("Choisis une date de fin."); return; }
    call(
      { duration: dur, until: dur === "custom" ? new Date(until).toISOString() : undefined, reason },
      (d) => (d.follow_locked_until ? `Compte non suivable jusqu'au ${fmt(d.follow_locked_until)}` : "Compte non suivable (illimité)"),
    );
  };

  return (
    <div className="rounded-xl border p-4 space-y-2" style={{ borderColor: "var(--yalola-border)" }} data-testid="follow-lock-card" data-active={active ? 1 : 0}>
      <div className="flex items-center gap-2 font-semibold text-sm"><UserX size={15} style={{ color: AMBER }} /> Compte non suivable</div>
      <p className="text-xs" style={MUTED}>
        Personne ne peut plus suivre ce compte ni lui envoyer de demande de suivi (les abonnés actuels sont conservés). Sanction silencieuse : le membre
        n'est pas prévenu. La levée est automatique à l'échéance choisie.
      </p>
      {active ? (
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="text-xs" style={{ color: AMBER }} data-testid="follow-lock-status" data-until={u.follow_locked_until || ""}>
            <b>Non suivable</b> {u.follow_locked_until ? `jusqu'au ${fmt(u.follow_locked_until)}` : "· illimité"}
            {u.follow_lock_reason && <span> · motif : {u.follow_lock_reason}</span>}
            {u.follow_locked_at && <span style={MUTED}> · depuis le {fmt(u.follow_locked_at)}</span>}
          </div>
          <Button variant="outline" className="rounded-full" onClick={() => call({ duration: "none" }, () => "Sanction levée : le compte peut de nouveau être suivi")} disabled={busy} data-testid="follow-lock-lift-btn">
            <Unlock size={14} className="mr-1" /> Lever maintenant
          </Button>
        </div>
      ) : (
        <div className="flex items-end gap-2 flex-wrap">
          <div>
            <Label className="text-xs">Durée</Label>
            <Select value={dur} onValueChange={setDur}>
              <SelectTrigger className="w-44" data-testid="follow-lock-duration"><SelectValue /></SelectTrigger>
              <SelectContent>
                {DURATIONS.map(([v, l]) => <SelectItem key={v} value={v} data-testid={`follow-lock-duration-${v}`}>{l}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {dur === "custom" && (
            <div>
              <Label className="text-xs">Jusqu'au</Label>
              <Input type="datetime-local" value={until} min={localMin()} onChange={(e) => setUntil(e.target.value)} className="w-56" data-testid="follow-lock-until" />
            </div>
          )}
          <Input placeholder="Motif interne (admins seulement)" value={reason} onChange={(e) => setReason(e.target.value)} className="flex-1 min-w-[180px]" data-testid="follow-lock-reason" />
          <Button variant="outline" className="rounded-full" style={{ color: AMBER }} onClick={lock} disabled={busy} data-testid="follow-lock-btn">
            <UserX size={14} className="mr-1" /> Rendre non suivable
          </Button>
        </div>
      )}
    </div>
  );
};
